import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  CheckBoxFieldv2,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
  NoteColors,
  NoteV2,
  PlanType,
  RadioButtonGroupMapping,
  RadioButtonsV2,
  SelectV2,
} from '@labrav/react-components'

import React, { useCallback, useMemo, useState } from 'react'
import { faBoxArchive, faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { Grid, Typography } from '@mui/material'
import { FormikHandlers, FormikProps, useFormikContext } from 'formik'
import { useStyles } from './FlyOutProductDimensionsPurchasingOptions.styles'
import { FormField } from '@labrav/react-components/lib/@types/types/forms'
import { productDimensionsPricingKeys } from '../../../common/utils/constants'

import { FormLockedType } from '../../../types/FormLockedType'
import {
  FlyoutListingProductType,
  FlyoutProductSections,
} from '../../../types/addEditProduct'
import {
  addDimensionCategoryNameFields,
  getProductTypeOptions,
  getpurchasingOptions,
  notEditableAwsFieldTooltip,
} from '../../../common/utils/productCreationUtil'

import { useSelector } from '../../../store/hooks'
import clsx from 'clsx'
import initialProductValues from '../../../mocks/initialProduct.json'
import { camelize } from 'casing'
import { isEmpty } from 'lodash'
import { AwsDimensionCategories } from '../../AddEditProduct/AddEditProductListing/FlyoutProductDimensionsSection/FlyoutProductDimensionsSection'
import { FlyoutProductDimensionsPurchasingOptionsFaqs } from './FlyoutProductDimensionsPurcashingOptionsFaqs'

const initialProductDimensions =
  camelize(initialProductValues).productDimensions

export const checkboxMappings: { [key: string]: string } = {
  '1 month': '1m',
  '12 Months': '12m',
  '24 Months': '24m',
  '36 Months': '36m',
}
export interface FlyOutProductDimensionsPurchasingOptionsProps {
  requirementsLink: string
  detailView?: boolean
  detailViewTooltip?: string
  productView?: boolean
  awsDimensionCategories: AwsDimensionCategories
}
export const FlyOutProductDimensionsPurchasingOptions: React.FC<
  FlyOutProductDimensionsPurchasingOptionsProps & FormLockedType
> = ({
  isFormLocked,
  detailView,
  detailViewTooltip,
  productView,
  awsDimensionCategories,
}) => {
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext() as FormikProps<FlyoutProductSections>

  const { classes } = useStyles()

  const productDimensions = values.productDimensions
  const pageMarker = values.pageMarker
  const productDimensionsErrors = errors.productDimensions
  const productDimensionsTouched = touched.productDimensions
  const productType = values.productDetails.productType
  const generalInformation = values.generalInformation
  const generalInformationErrors = errors.generalInformation
  const generalInformationTouched = touched.generalInformation

  const [openProductTypeConfirm, setOpenProductTypeConfirm] =
    useState<boolean>(false)
  const [selectedProductTypeForModal, setSelectedProductTypeForModal] =
    useState<string>('')

  const listingType = useSelector<FlyoutListingProductType>(
    state => state.flyoutProductInCreation.currentProductInCreation.listingType
  )

  const purchasingOptions: RadioButtonGroupMapping[] = getpurchasingOptions(
    !!isFormLocked || !!productView
  )

  const { contract, subscription } = awsDimensionCategories
  const dimensionUnitTypeList =
    productType === PlanType.SAAS_CONTRACT ? contract : subscription

  const addDimensionCategoryName: FormField = {
    ...addDimensionCategoryNameFields,
    options: dimensionUnitTypeList,
    isReadOnly: !!isFormLocked,
  }

  const handleCheckboxChange =
    (handleChange: FormikHandlers['handleChange']) => (title: string) => {
      return (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target
        const valueToAddOrRemove = checkboxMappings[title]
        if (valueToAddOrRemove) {
          const currentValues = productDimensions?.contractDuration || []
          const currentDimensionsPageMarker = pageMarker || {}

          if (checked) {
            currentValues.push(valueToAddOrRemove)
            setFieldValue('pageMarker', {
              ...currentDimensionsPageMarker,
              ['page2']: false,
            })
          } else {
            const indexToRemove = currentValues.indexOf(valueToAddOrRemove)
            if (indexToRemove !== -1) {
              currentValues.splice(indexToRemove, 1)
            }
            const keyToNullify =
              productDimensionsPricingKeys[
                valueToAddOrRemove as keyof typeof productDimensionsPricingKeys
              ]
            const currentDimensions = (productDimensions?.dimensions || []).map(
              dimension => {
                return { ...dimension, [keyToNullify]: null }
              }
            )
            setFieldValue('productDimensions.dimensions', currentDimensions)
          }
          handleChange({
            target: {
              name: 'productDimensions.contractDuration',
              value: currentValues,
            },
          })
        }
      }
    }

  const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedOptionKey = (event.target as HTMLInputElement).value as string
    if (selectedOptionKey === 'multipleDimensions') {
      setFieldValue('generalInformation.multipleDimensionsAllowed', true)
    } else if (selectedOptionKey === 'singleDimension') {
      setFieldValue('generalInformation.multipleDimensionsAllowed', false)
    } else if (selectedOptionKey === '') {
      setFieldValue('generalInformation.multipleDimensionsAllowed', null)
    }
  }

  const isNewOrManaged = listingType !== FlyoutListingProductType.MIGRATED
  const isSaasSubscription = productType === PlanType.SAAS_SUBSCRIPTION
  const isProfessionalService = productType === PlanType.PROFESSIONAL_SERVICES

  const productTypeOption = getProductTypeOptions(
    !!isFormLocked || !!productView
  )

  const checkedValue = useMemo(
    () =>
      generalInformation.multipleDimensionsAllowed
        ? 'multipleDimensions'
        : generalInformation.multipleDimensionsAllowed === false
          ? 'singleDimension'
          : '',
    [generalInformation.multipleDimensionsAllowed]
  )

  const handleChangeProductType = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = (event.target as HTMLInputElement).value as string
      if (isEmpty(productType)) {
        await setFieldValue('productDetails.productType', value)
      } else {
        await setSelectedProductTypeForModal(value)
        await setOpenProductTypeConfirm(true)
      }
    },
    [productType]
  )

  const handleCloseModal = async () => {
    await setSelectedProductTypeForModal('')
    await setOpenProductTypeConfirm(false)
  }

  const resetProductDimensions = async () => {
    await setFieldValue('productDimensions', { ...initialProductDimensions })
    await setFieldTouched('productDimensions', false)
    await setFieldValue('generalInformation.multipleDimensionsAllowed', null)
  }

  const handleSubmitModal = async () => {
    await setFieldValue(
      'productDetails.productType',
      selectedProductTypeForModal
    )
    await resetProductDimensions()
    await handleCloseModal()
  }

  const tooltipValue = detailView
    ? detailViewTooltip
    : productView
      ? notEditableAwsFieldTooltip
      : ''

  const [indexModal, setIndexModal] = useState(0)

  return isNewOrManaged && !isProfessionalService ? (
    <BoxV2
      heading={'Purchasing options'}
      headingIcon={
        <FontAwesomeIcon
          icon={faListCheck}
          style={{ width: '18px', height: '14px' }}
        />
      }
      secondaryActions={
        <FlyoutProductDimensionsPurchasingOptionsFaqs
          indexModal={indexModal}
          setIndexModal={setIndexModal}
        />
      }
    >
      <div className={classes.sectionContainer}>
        <div className={classes.textContainer}>
          <Typography className={classes.sectionHeading}>
            Which pricing model do you intend to list?
          </Typography>
          <Typography className={classes.sectionText}>
            The pricing model that you choose determines how buyers are able to
            purchase your product.
          </Typography>
        </div>
        <LabraToolTipV2
          followCursor={productView}
          tooltipPlacement="bottom-start"
          description={tooltipValue}
        >
          <RadioButtonsV2
            name={
              'infoCollectionFormMetaData.redirectionSettings.isPublicFacingSignupPageAvailable'
            }
            checkedValue={{
              key: values.productDetails.productType,
              value: values.productDetails.productType,
            }}
            handleChange={handleChangeProductType}
            radioButtonMappings={productTypeOption}
            border
            alignSameHeight
          />
        </LabraToolTipV2>
      </div>

      {!isEmpty(values.productDetails.productType) && (
        <>
          <div className={classes.sectionContainer}>
            <div className={classes.textContainer}>
              <Typography className={classes.sectionHeading}>
                Dimension unit type
              </Typography>
              <Typography className={classes.sectionText}>
                Choose the unit type that best describes how your product will
                be priced.
              </Typography>
            </div>
            <NoteV2
              description={
                'If your unit of measurement varies by dimension or you’re uncertain whether the unit type will change in future, we recommend choosing the generic ‘Units’ option, as this cannot be changed later.'
              }
            />
            <div className={classes.field}>
              <LabraToolTipV2
                followCursor={productView}
                tooltipPlacement="bottom-start"
                description={tooltipValue}
              >
                <SelectV2
                  field={{ ...addDimensionCategoryName }}
                  value={productDimensions?.dimensionCategoryName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isError={
                    !!productDimensionsErrors?.dimensionCategoryName &&
                    !!productDimensionsTouched?.dimensionCategoryName
                  }
                  errorText={
                    (!!productDimensionsTouched?.dimensionCategoryName &&
                      productDimensionsErrors?.dimensionCategoryName) ||
                    ''
                  }
                  viewOnly={!!productView}
                />
              </LabraToolTipV2>
            </div>
          </div>

          {!isSaasSubscription && (
            <>
              <div className={classes.sectionContainer}>
                <div className={classes.textContainer}>
                  <Typography className={classes.sectionHeading}>
                    Purchasing options
                  </Typography>
                  <Typography className={classes.sectionText}>
                    Specify how you want buyers to be able to purchase your
                    product.
                  </Typography>
                </div>
                <LabraToolTipV2
                  followCursor={productView}
                  tooltipPlacement="bottom-start"
                  description={tooltipValue}
                >
                  <RadioButtonsV2
                    checkedValue={{
                      key: checkedValue,
                      value: checkedValue,
                    }}
                    name="generalInformation.multipleDimensionsAllowed"
                    radioButtonMappings={purchasingOptions}
                    handleChange={handleSelection}
                  />
                </LabraToolTipV2>
                {!!generalInformationTouched?.multipleDimensionsAllowed &&
                  generalInformationErrors?.multipleDimensionsAllowed && (
                    <div className={classes.errorMessage}>
                      {generalInformationErrors?.multipleDimensionsAllowed}
                    </div>
                  )}
              </div>
              <div className={classes.sectionContainer}>
                <div className={classes.textContainer}>
                  <Typography className={classes.sectionHeading}>
                    Contract duration options
                  </Typography>
                  <Typography className={classes.sectionText}>
                    Choose contract durations you want to make publicly
                    available to buyers.
                  </Typography>
                </div>
                <LabraToolTipV2
                  followCursor={productView}
                  tooltipPlacement="bottom-start"
                  description={tooltipValue}
                  className={classes.checkboxContainerTooltip}
                >
                  <Grid container spacing={3} width={'100%'}>
                    {Object.keys(checkboxMappings).map(title => (
                      <Grid
                        className={classes.checkboxWidth}
                        size={{
                          xs: 12,
                          md: 6,
                          lg: 3,
                        }}
                      >
                        <CheckBoxFieldv2
                          keyName="productDimensions.contractDuration"
                          title={title}
                          value={productDimensions?.contractDuration?.includes(
                            checkboxMappings[title]
                          )}
                          onChangeBox={handleCheckboxChange(handleChange)(
                            title
                          )}
                          disabled={isFormLocked || productView}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {!!productDimensionsTouched?.contractDuration &&
                    productDimensionsErrors?.contractDuration && (
                      <div className={classes.errorMessage}>
                        {(!!productDimensionsTouched?.contractDuration &&
                          productDimensionsErrors?.contractDuration) ||
                          ''}
                      </div>
                    )}
                </LabraToolTipV2>
              </div>
            </>
          )}
        </>
      )}
      <ModalConfirmV2
        variant={ModalSizeVariants.small}
        open={openProductTypeConfirm}
        title={'Change product category'}
        titleAcceptButton={'Change product type'}
        onDecline={handleCloseModal}
        onAccept={handleSubmitModal}
        content={
          <>
            <div
              className={clsx(
                classes.productTypeChangeText,
                classes.productTypeChangeConfirmText
              )}
            >
              Are you sure you want to change your product category?
            </div>
            <NoteV2
              backgroundColor={NoteColors.YELLOW}
              description={
                'Your data will be lost and you will have to fill this page again.'
              }
            />
          </>
        }
        icon={
          <FontAwesomeIcon
            icon={faBoxArchive}
            style={{ width: '20px', height: '16px' }}
          />
        }
      />
    </BoxV2>
  ) : null
}
